import { Inject, Injectable } from '@angular/core';
import {
  Firestore,
  collection,
  doc,
  setDoc,
  deleteDoc,
  query,
  where,
  getDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { collectionData } from 'rxfire/firestore';
import { combineLatest, from, map, Observable, of, switchMap } from 'rxjs';
import { FIRESTORE } from '../firebase-injection';
import { UserGroup, userGroupConverter } from '../models/user-group.model';
import { User, UserId } from '../models/user.model';
import { UserService } from './user.service';
import { DeckService } from './deck.service';
import { Deck } from '../models/deck.model';
import { PublicDeckService } from './public-deck.service';
import {
  deckLogEntryConverter,
  DeckLogEntry,
} from '../models/deck-log-entry.model';
import { UserGroupFullInformation } from '../models/user-group-full-information.model';

@Injectable({
  providedIn: 'root',
})
export class UserGroupService {
  constructor(
    @Inject(FIRESTORE) private firestore: Firestore,
    private userService: UserService,
    private deckService: DeckService,
    private publicDeckService: PublicDeckService
  ) {}

  public addUserGroup(userGroup: UserGroup): Promise<void> {
    const userGroupRef = doc(
      this.firestore,
      'userGroups',
      userGroup.id
    ).withConverter(userGroupConverter);
    return setDoc(userGroupRef, userGroup);
  }

  public updateUserGroup(userGroup: UserGroup): Promise<void> {
    const userGroupRef = doc(
      this.firestore,
      'userGroups',
      userGroup.id
    ).withConverter(userGroupConverter);
    return setDoc(userGroupRef, userGroup);
  }

  public getUserGroup(userGroupId: string): Observable<UserGroup> {
    if (!userGroupId) {
      throw new Error('Invalid userGroupId: must not be empty');
    }

    const userGroupRef = doc(
      this.firestore,
      `userGroups/${userGroupId}`
    ).withConverter(userGroupConverter);

    return new Observable<UserGroup>((subscriber) => {
      getDoc(userGroupRef)
        .then((docSnap) => {
          if (docSnap.exists()) {
            subscriber.next(docSnap.data() as UserGroup);
            subscriber.complete();
          } else {
            subscriber.error('UserGroup not found');
          }
        })
        .catch((error) => subscriber.error(error));
    });
  }

  public getCreatedGroupsOfUser(userId: UserId): Observable<UserGroup[]> {
    const userGroupsCol = collection(
      this.firestore,
      'userGroups'
    ).withConverter(userGroupConverter);
    const q = query(userGroupsCol, where('authorId', '==', userId));
    return collectionData(q, { idField: 'id' }) as Observable<UserGroup[]>;
  }

  public getContributedGroupsOfUser(userId: UserId): Observable<UserGroup[]> {
    const userGroupsCol = collection(
      this.firestore,
      'userGroups'
    ).withConverter(userGroupConverter);
    const q = query(
      userGroupsCol,
      where('contributorIds', 'array-contains', userId)
    );
    return collectionData(q, { idField: 'id' }) as Observable<UserGroup[]>;
  }

  public getPendingContributedGroupsOfUser(
    userId: UserId
  ): Observable<UserGroup[]> {
    const userGroupsCol = collection(
      this.firestore,
      'userGroups'
    ).withConverter(userGroupConverter);
    const q = query(
      userGroupsCol,
      where('pendingContributorIds', 'array-contains', userId)
    );
    return collectionData(q, { idField: 'id' }) as Observable<UserGroup[]>;
  }

  public getWatchedGroupsOfUser(userId: UserId): Observable<UserGroup[]> {
    const userGroupsCol = collection(
      this.firestore,
      'userGroups'
    ).withConverter(userGroupConverter);
    const q = query(
      userGroupsCol,
      where('watcherIds', 'array-contains', userId)
    );
    return collectionData(q, { idField: 'id' }) as Observable<UserGroup[]>;
  }

  public getPendingWatchedGroupsOfUser(
    userId: UserId
  ): Observable<UserGroup[]> {
    const userGroupsCol = collection(
      this.firestore,
      'userGroups'
    ).withConverter(userGroupConverter);
    const q = query(
      userGroupsCol,
      where('pendingWatcherIds', 'array-contains', userId)
    );
    return collectionData(q, { idField: 'id' }) as Observable<UserGroup[]>;
  }

  /*  In groups, we store only the IDs of the decks, authors, contributors, and watchers.
   * This function is useful when we want to display the full information of a group.
   */
  public getFullGroupInformation(
    userGroupId: string
  ): Observable<UserGroupFullInformation> {
    return this.getUserGroup(userGroupId).pipe(
      switchMap((group) => {
        const author$ = from(this.userService.getUser(group.authorId)).pipe(
          map((user) => {
            if (!user) throw new Error('Author not found');
            return user;
          })
        );

        const contributors$ = (group.contributorIds || []).length
          ? combineLatest(
              group.contributorIds!.map((id) =>
                from(this.userService.getUser(id))
              )
            ).pipe(map((users) => users.filter((u): u is User => !!u)))
          : of([]);

        const watchers$ = (group.watcherIds || []).length
          ? combineLatest(
              group.watcherIds!.map((id) => from(this.userService.getUser(id)))
            ).pipe(map((users) => users.filter((u): u is User => !!u)))
          : of([]);

        const pendingWatchers$ = (group.pendingWatcherIds || []).length
          ? combineLatest(
              group.pendingWatcherIds.map((id) =>
                from(this.userService.getUser(id))
              )
            ).pipe(map((users) => users.filter((u): u is User => !!u)))
          : of([]);

        const pendingContributors$ = (group.pendingContributorIds || []).length
          ? combineLatest(
              group.pendingContributorIds.map((id) =>
                from(this.userService.getUser(id))
              )
            ).pipe(map((users) => users.filter((u): u is User => !!u)))
          : of([]);

        const decks$ = (group.deckIds || []).length
          ? combineLatest(
              group.deckIds!.map((deckId) =>
                this.deckService.getDeckOfUser(group.authorId, deckId).pipe(
                  map((deck) => {
                    if (!deck) {
                      this.removeDeckFromGroup(userGroupId, deckId).subscribe(
                        () => {
                          console.error(
                            'Deck not found and removed from group'
                          );
                        }
                      );
                    }
                    return deck;
                  })
                )
              )
            ).pipe(map((decks) => decks.filter((deck): deck is Deck => !!deck)))
          : of([]);

        return combineLatest([
          author$,
          contributors$,
          watchers$,
          pendingWatchers$,
          pendingContributors$,
          decks$,
        ]).pipe(
          map(
            ([
              author,
              contributors,
              watchers,
              pendingWatchers,
              pendingContributors,
              decks,
            ]) => ({
              id: group.id,
              name: group.name,
              description: group.description,
              created: group.created,
              author,
              contributors,
              watchers,
              pendingWatchers,
              pendingContributors,
              decks,
              deckLogs: group.deckLogs ?? [],
            })
          )
        );
      })
    );
  }

  public deleteUserGroup(userGroupId: string): Promise<void> {
    const userGroupRef = doc(this.firestore, 'userGroups', userGroupId);
    return deleteDoc(userGroupRef);
  }

  public checkUserRole(groupId: string, userId: string): Observable<string> {
    return this.getFullGroupInformation(groupId).pipe(
      map((group) => {
        if (group.author.id === userId) {
          return 'author';
        }
        for (const contributor of group.contributors || []) {
          if (contributor.id === userId) {
            return 'contributor';
          }
        }
        for (const watcher of group.watchers || []) {
          if (watcher.id === userId) {
            return 'watcher';
          }
        }
        for (const pendingContributor of group.pendingContributors || []) {
          if (pendingContributor.id === userId) {
            return 'pendingContributor';
          }
        }
        for (const pendingWatcher of group.pendingWatchers || []) {
          if (pendingWatcher.id === userId) {
            return 'pendingWatcher';
          }
        }
        return 'none';
      })
    );
  }

  public inviteUser(
    userGroupId: string,
    userEmail: string,
    type: 'watcher' | 'contributor'
  ): Observable<void> {
    return this.userService.getUserByMail(userEmail).pipe(
      switchMap((user) => {
        if (!user) {
          throw new Error('User not found');
        }

        return this.checkUserRole(userGroupId, user.id).pipe(
          switchMap((role) => {
            if (role !== 'none') {
              throw new Error(`User already has a role: ${role}`);
            }

            const userGroupRef = doc(
              this.firestore,
              'userGroups',
              userGroupId
            ).withConverter(userGroupConverter);

            return from(getDoc(userGroupRef)).pipe(
              switchMap((docSnap) => {
                if (!docSnap.exists()) {
                  throw new Error('UserGroup not found');
                }

                const userGroup = docSnap.data() as UserGroup;

                // Update the appropriate field
                if (type === 'contributor') {
                  userGroup.pendingContributorIds.push(user.id);
                } else if (type === 'watcher') {
                  userGroup.pendingWatcherIds.push(user.id);
                }

                return from(setDoc(userGroupRef, userGroup));
              })
            );
          })
        );
      })
    );
  }

  cancelInvitation(
    groupId: string,
    userId: string,
    type: string
  ): Observable<void> {
    const userGroupRef = doc(
      this.firestore,
      'userGroups',
      groupId
    ).withConverter(userGroupConverter);

    return from(getDoc(userGroupRef)).pipe(
      switchMap((docSnap) => {
        if (!docSnap.exists()) {
          throw new Error('UserGroup not found');
        }

        const userGroup = docSnap.data() as UserGroup;

        if (type === 'contributor') {
          userGroup.pendingContributorIds =
            userGroup.pendingContributorIds.filter((id) => id !== userId);
        } else if (type === 'watcher') {
          userGroup.pendingWatcherIds = userGroup.pendingWatcherIds.filter(
            (id) => id !== userId
          );
        }

        return from(setDoc(userGroupRef, userGroup));
      })
    );
  }

  public acceptInvitation(
    groupId: string,
    userId: string,
    type: string
  ): Observable<void> {
    const userGroupRef = doc(
      this.firestore,
      'userGroups',
      groupId
    ).withConverter(userGroupConverter);

    return from(getDoc(userGroupRef)).pipe(
      switchMap((docSnap) => {
        if (!docSnap.exists()) {
          throw new Error('UserGroup not found');
        }

        const userGroup = docSnap.data() as UserGroup;

        if (userGroup.contributorIds && type === 'contributor') {
          userGroup.contributorIds.push(userId);
          userGroup.pendingContributorIds =
            userGroup.pendingContributorIds.filter((id) => id !== userId);
        } else if (userGroup.watcherIds && type === 'watcher') {
          userGroup.watcherIds.push(userId);
          userGroup.pendingWatcherIds = userGroup.pendingWatcherIds.filter(
            (id) => id !== userId
          );
        }

        return from(setDoc(userGroupRef, userGroup));
      })
    );
  }

  public removeUserFromGroup(
    groupId: string,
    userId: string
  ): Observable<UserGroup> {
    const userGroupRef = doc(
      this.firestore,
      'userGroups',
      groupId
    ).withConverter(userGroupConverter);

    return from(getDoc(userGroupRef)).pipe(
      switchMap((docSnap) => {
        if (!docSnap.exists()) {
          throw new Error('UserGroup not found');
        }

        const userGroup = docSnap.data() as UserGroup;

        userGroup.contributorIds = userGroup.contributorIds?.filter(
          (id) => id !== userId
        );
        userGroup.watcherIds = userGroup.watcherIds?.filter(
          (id) => id !== userId
        );
        userGroup.pendingContributorIds =
          userGroup.pendingContributorIds?.filter((id) => id !== userId);
        userGroup.pendingWatcherIds = userGroup.pendingWatcherIds?.filter(
          (id) => id !== userId
        );

        if (userGroup.deckLogs) {
          userGroup.deckLogs = userGroup.deckLogs.filter(
            (entry) => entry.userId !== userId
          );
        }

        return from(setDoc(userGroupRef, userGroup)).pipe(map(() => userGroup));
      })
    );
  }

  public removeDeckFromGroup(
    groupId: string,
    deckId: string
  ): Observable<void> {
    const userGroupRef = doc(
      this.firestore,
      'userGroups',
      groupId
    ).withConverter(userGroupConverter);

    return from(getDoc(userGroupRef)).pipe(
      switchMap((docSnap) => {
        if (!docSnap.exists()) {
          throw new Error('UserGroup not found');
        }

        const userGroup = docSnap.data() as UserGroup;

        userGroup.deckIds = userGroup.deckIds?.filter(
          (deck) => deck !== deckId
        );

        if (userGroup.deckLogs) {
          userGroup.deckLogs = userGroup.deckLogs.filter(
            (entry) => entry.deckId !== deckId
          );
        }

        return from(setDoc(userGroupRef, userGroup));
      })
    );
  }

  public addDeckToGroup(groupId: string, deckId: string): Observable<void> {
    const userGroupRef = doc(
      this.firestore,
      'userGroups',
      groupId
    ).withConverter(userGroupConverter);

    return from(getDoc(userGroupRef)).pipe(
      switchMap((docSnap) => {
        if (!docSnap.exists()) {
          throw new Error('UserGroup not found');
        }

        const userGroup = docSnap.data() as UserGroup;

        if (userGroup.deckIds?.includes(deckId)) {
          return of();
        }

        userGroup.deckIds = [...(userGroup.deckIds || []), deckId];

        return from(setDoc(userGroupRef, userGroup));
      })
    );
  }

  public groupsOfDeck(deckId: string): Observable<string[]> {
    const userGroupsCol = collection(
      this.firestore,
      'userGroups'
    ).withConverter(userGroupConverter);

    return collectionData(userGroupsCol, { idField: 'id' }).pipe(
      map((groups: UserGroup[]) => {
        const groupIds: string[] = groups
          .filter((group) => group.deckIds && group.deckIds.includes(deckId))
          .map((group) => group.id);
        return groupIds;
      })
    );
  }

  public addUserByInviteLink(
    groupId: string,
    userId: string,
    type: 'watcher' | 'contributor',
    token: string
  ): Observable<void> {
    const userGroupRef = doc(this.firestore, 'userGroups', groupId);

    return from(getDoc(userGroupRef)).pipe(
      switchMap((docSnap) => {
        if (!docSnap.exists()) {
          throw new Error('UserGroup not found');
        }
        const data = docSnap.data();
        const stored =
          type === 'contributor'
            ? data['inviteTokenContributor']
            : data['inviteTokenWatcher'];
        if (!stored || stored !== token) {
          throw new Error('Invalid invite token');
        }
        return from(
          updateDoc(userGroupRef, {
            [type === 'contributor' ? 'contributorIds' : 'watcherIds']:
              arrayUnion(userId),
            pendingWatcherIds: arrayRemove(userId),
            pendingContributorIds: arrayRemove(userId),
          })
        );
      })
    );
  }

  public ensureInviteToken(
    groupId: string,
    type: 'watcher' | 'contributor'
  ): Observable<string> {
    const userGroupRef = doc(
      this.firestore,
      'userGroups',
      groupId
    ).withConverter(userGroupConverter);
    const tokenField =
      type === 'contributor' ? 'inviteTokenContributor' : 'inviteTokenWatcher';

    return from(getDoc(userGroupRef)).pipe(
      switchMap((docSnap) => {
        if (!docSnap.exists()) {
          throw new Error('UserGroup not found');
        }
        const userGroup = docSnap.data() as UserGroup;
        const existing =
          type === 'contributor'
            ? userGroup.inviteTokenContributor
            : userGroup.inviteTokenWatcher;
        if (existing) {
          return of(existing);
        }
        const fresh = this.generateInviteToken();
        return from(
          updateDoc(doc(this.firestore, 'userGroups', groupId), {
            [tokenField]: fresh,
          })
        ).pipe(map(() => fresh));
      })
    );
  }

  public rotateInviteToken(
    groupId: string,
    type: 'watcher' | 'contributor'
  ): Observable<string> {
    const tokenField =
      type === 'contributor' ? 'inviteTokenContributor' : 'inviteTokenWatcher';
    const fresh = this.generateInviteToken();
    return from(
      updateDoc(doc(this.firestore, 'userGroups', groupId), {
        [tokenField]: fresh,
      })
    ).pipe(map(() => fresh));
  }

  private generateInviteToken(): string {
    const bytes = new Uint8Array(24);
    crypto.getRandomValues(bytes);
    let binary = '';
    for (const byte of bytes) {
      binary += String.fromCharCode(byte);
    }
    return btoa(binary)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  public addLogEntry(
    logEntry: DeckLogEntry,
    groupIds: string[]
  ): Observable<void> {
    const updateObservables = groupIds.map((groupId) => {
      const userGroupRef = doc(
        this.firestore,
        'userGroups',
        groupId
      ).withConverter(userGroupConverter);

      return from(getDoc(userGroupRef)).pipe(
        switchMap((docSnap) => {
          if (!docSnap.exists()) {
            throw new Error(`UserGroup with ID ${groupId} not found`);
          }

          const userGroup = docSnap.data() as UserGroup;

          // Add the log entry
          userGroup.deckLogs = userGroup.deckLogs || [];
          userGroup.deckLogs.push(deckLogEntryConverter.toFirestore(logEntry));

          return from(setDoc(userGroupRef, userGroup));
        })
      );
    });

    return combineLatest(updateObservables).pipe(map(() => {}));
  }
}
